import { SendOutlined } from '@ant-design/icons';
import { Modal, ModalProps, Select } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { JobPostingDetail } from '../types';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';

export interface SendCustomTestsFormValues extends EmailFormValues {
  surveys: number[];
}

export interface SendCustomTestsModalProps extends ModalProps {
  jobPostingId?: JobPostingDetail['id'];
  loading?: boolean;
  onFinish?: (values: SendCustomTestsFormValues) => void;
  helmetTitle: string;
}

const SendCustomTestsModal = ({
  jobPostingId,
  helmetTitle,
  loading,
  onFinish,
  ...props
}: SendCustomTestsModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const user = useAPIQuery('/users/me/');

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'CUSTOM_TESTS',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  const surveys = useAPIQuery([
    '/alchemer/survey/',
    {
      params: {
        'filter[field][1]': 'status',
        'filter[operator][1]': '=',
        'filter[value][1]': 'launched',
      },
    },
  ]);

  const config = useAPIQuery('/configs/tenant-configurations/');

  return (
    <Modal
      okButtonProps={{ htmlType: 'submit', icon: <SendOutlined />, loading }}
      okText={t('Enviar')}
      onOk={() => form.submit()}
      {...props}
    >
      <Helmet>
        <title>
          {helmetTitle} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<SendCustomTestsFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          subject: '',
          message: '',
          fromName: config.data?.companyFromName || '',
          replyTo: user.data?.email,
          enableReplyTo: false,
          email_communication_template: null,
          attachments: [],
          surveys: [],
        }}
        onFinish={(variables) =>
          onFinish?.({ ...variables, message: fixUnorderedListInQuill(variables.message) })
        }
      >
        <Form.Item
          name="surveys"
          label={`${t('Selecione')}:`}
          rules={[{ min: 1, type: 'array', message: t('Selecione no mínimo um teste') }]}
        >
          <Select mode="multiple" allowClear style={{ width: '100%' }}>
            {surveys.data?.data.map(({ id, title }) => (
              <Select.Option key={id} value={id}>
                {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <EmailForm
          templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
            id: emailCommunicationTemplate.id,
            name: emailCommunicationTemplate.name,
            message: emailCommunicationTemplate.message,
            subject: emailCommunicationTemplate.subject,
            fromName: emailCommunicationTemplate.from_name,
            attachments: emailCommunicationTemplate.attachments,
          }))}
          customVariables={[
            {
              value: '[link_testes_customizados]',
              label: t('Link Testes Customizados'),
            },
            { value: '[candidato]', label: t('Nome do Candidato') },
            { value: '[empresa]', label: t('Nome da Empresa') },
            ...(jobPostingId ? [{ value: '[vaga]', label: t('Nome da Vaga') }] : []),
          ]}
          richEditorRules={[
            { message: t('Campo obrigatório.'), required: true },
            {
              validator(_, value) {
                const variablesRegex = /\[vaga\]/;
                if (!jobPostingId && variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      t(
                        'Não é possível utilizar a variável [vaga] ao enviar email pelo Banco de Talentos'
                      )
                    )
                  );
                }
                return Promise.resolve();
              },
            },
            {
              validator(_, value) {
                const variablesRegex = /\[link_testes_customizados\]/;
                if (!variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      t('Para enviar este e-mail é necessário a variável Link Testes Customizados')
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          image
          attachments
          fromName
          replyTo
        />
      </Form>
    </Modal>
  );
};

export default SendCustomTestsModal;
